import { ChangeDetectionStrategy, Component, OnChanges, OnInit, SimpleChanges, computed, inject, input, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem, TreeTableNode } from 'primeng/api';
import { TreeTableHeaderCheckboxToggleEvent, TreeTableLazyLoadEvent } from 'primeng/treetable';
import { AppConfig } from 'src/app/app-config.loader';
import { GassClaims } from 'src/app/auth/gass-claims';
import { IdentityService } from 'src/app/auth/identity.service';
import { AllegatiService } from 'src/app/core/allegati.service';
import { DocumentiService } from 'src/app/core/documenti.service';
import { HierarchyService } from 'src/app/core/hierarchy.service';
import { NuovoDocumentoService } from 'src/app/core/nuovo-documento.service';
import { environment } from 'src/environments/environment';
import { DistruzioneAllegato } from '../../models/DistruzioneRequest.model';
import { DocumentiSearchAllegato, DocumentiSearchDocRow, DocumentiSearchPosizioneRow, DocumentiSearchRequest, DocumentiSearchResponse, DocumentiSearchResultRow, TypedTreeNode } from '../../models/documento-search.model';
import { DocumentoAllegato } from '../../models/documentoAllegato.model';
import { StatiPredefiniti } from '../../models/stati-predefiniti';
import { DocumentiListColumnMetadata, DocumentiListColumnNames } from './documenti-list.model';

@Component({
  selector: 'app-documenti-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './documenti-list.component.html',
  styleUrls: ['./documenti-list.component.scss']
})
export class DocumentiListComponent implements OnInit, OnChanges {
  data = input.required<DocumentiSearchResponse>();
  activeColumns = input.required<DocumentiListColumnNames>();
  allowSort = input(true);
  allowPosizioneHierarchy = input(true);
  allowSelection = input(false);
  allowCheckBoxSelection = input(false);
  enableDateExpirationCss = input(false);
  showDeclassificaButton = input(false);
  showDistruggiButton = input(false);
  showActionButtons = input(false);
  showEvidenzaButtons = input(false);
  showDeleteButton = input(false);
  paginator = input(true);
  showRowsPerPage = input(true);

  updateRequestAndSearch = output<DocumentiSearchRequest | null>();
  rowSelected = output<DocumentiSearchDocRow | DocumentiSearchDocRow[]>();
  declassify = output<DocumentiSearchDocRow>();
  destroy = output<DocumentiSearchDocRow>();
  delete = output<DocumentiSearchDocRow>();

  public items: MenuItem[][] = [];
  public allegati: DocumentoAllegato[] = [];
  public GassClaims = GassClaims;
  public allExpanded = true;
  public StatiPredefiniti = StatiPredefiniti;
  public activeColumnMetadata: DocumentiListColumnMetadata[] = [];
  public selectedRow: TreeTableNode<DocumentiSearchDocRow> | TreeTableNode<DocumentiSearchDocRow>[] | null = null;
  public hierarchyFascicoloLevel = 1;
  public hierarchy: string[] = [];
  public showDeclassificaDialog = signal<boolean>(false);
  public showStateArchiveDialog = signal<boolean>(false);
  public showDistruzioneDialog = signal<boolean>(false);
  public showModificaStatoDialog = signal<boolean>(false)
  public showCopyDialog = signal<boolean>(false);

  public actionableRow: DocumentiSearchDocRow | undefined;
  public distruzioneAllegato: DistruzioneAllegato[] | undefined;
  public actionButtons: MenuItem[];

  public pageSize = computed<number>( ()=> {
    return (this.data().originalRequest.pageSize>0)?this.data().originalRequest.pageSize:10;
  });

  public isLazy = computed<boolean>( ()=> {
    return (this.data().originalRequest.pageSize>0);
  });

  private readonly router = inject(Router);
  private readonly documentiService = inject(DocumentiService);
  private readonly nuovoDocumentoService = inject(NuovoDocumentoService);
  private readonly identityService = inject(IdentityService);
  private readonly allegatiService = inject(AllegatiService);
  private readonly appConfig = inject(AppConfig);
  private readonly hierarchyService = inject(HierarchyService);

  private readonly allColumnMetadata: DocumentiListColumnMetadata[] = [
    { name: 'PosizioneId', propName: 'posizioneId', type: 'PosizioneId', width: '150px', sortable: false, },
    {
      name: 'Protocollo', propName: 'protocollo', type: 'StringLink', width: '180px',
      linkUrlBuilder: rowData => this.identityService.hasClaim(GassClaims.DocConsultazione) ? ['/visualizza', rowData.documentoId] : null, sortable: true,
    },
    { name: 'DataDocumento', propName: 'dataDocumento', type: 'Date', width: '90px', title: 'Data', sortable: true, },
    { name: 'Stato', propName: 'stato', type: 'String', width: '90px', sortable: true, },
    { name: 'StatoDal', propName: 'statoDal', type: 'Date', width: '90px', title: 'Dal', sortable: false, },
    { name: 'StatoUtente', propName: 'statoUtente', type: 'String', width: '180px', title: 'Utente', sortable: false, },
    { name: 'Classifica', propName: 'classifica', type: 'String', width: '180px', sortable: true, },
    { name: 'Qualifica', propName: 'qualifica', type: 'String', width: '180px', sortable: true, },
    { name: 'Arrivo', propName: 'arrivo', type: 'Arrivo', width: '74px', sortable: true, },
    { name: 'Oggetto', propName: 'oggetto', type: 'String', sortable: true, },
    { name: 'Note', propName: 'note', type: 'String', sortable: true, },
    { name: 'CopiePer', propName: 'copiePer', type: 'CopiePer', width: '90px', title: 'Copie', sortable: true, },
    { name: 'DataScadenza', propName: 'dataScadenza', type: 'Date', width: '110px', title: 'Scadenza', sortable: true, },
    { name: 'Mittente', propName: 'mittente', type: 'String', width: '140px', sortable: true, },
    { name: 'Destinatari', propName: 'destinatari', type: 'StringArray', width: '140px', sortable: true, },
    { name: 'ActionButtons', type: 'ActionButtons', width: '150px', sortable: true, },
    { name: 'DataScadenzaClassifica', propName: 'dataScadenzaClassifica', type: 'Date', width: '200px', title: 'Data scadenza classifica', sortable: true, },
  ];

  constructor() {
    this.actionButtons = [
      {
        label: 'Eleva/Declassifica',
        icon: 'fas fa-fw fa-file-download',
        state: { stato: StatiPredefiniti.Declassificato },
        disabled: !this.identityService.hasClaim(GassClaims.DocModifica),
        command: () => {
          this.showDeclassifica();
        }
      },
      {
        label: 'Versamento',
        icon: 'fas fa-fw fa-landmark',
        state: { stato: StatiPredefiniti.Versato },
        disabled: !this.identityService.hasClaim(GassClaims.DocModifica),
        command: () => {
          this.showStateArchive();
        }
      },
      {
        label: 'Distruzione',
        icon: 'fas fa-fw fa-trash',
        styleClass: 'p-button-danger',
        state: { stato: StatiPredefiniti.Distrutto },
        disabled: !this.identityService.hasClaim(GassClaims.DocDistruzione),
        command: () => {
          this.showDistruzione();
        }
      }
    ];
  }

  get isHierarchyMode(): boolean {
    return this.data().originalRequest?.sortField === 'PosizioneId';
  }

  get folderColSpan(): number {
    if (this.activeColumnMetadata.some(c => c.name === 'ActionButtons')) {
      return this.activeColumnMetadata.length - 2;
    }

    return this.activeColumnMetadata.length - 1;
  }

  get selectionMode(): string | undefined {
    if (this.allowSelection())
      return 'single';

    if (this.allowCheckBoxSelection())
      return 'checkbox';

    return undefined
  }

  ngOnInit(): void {
    this.detectedAllegati();
    this.hierarchyFascicoloLevel = this.appConfig.logic.hierarchy.descriptionIndex + 1;
    this.hierarchy = this.hierarchyService.getHierarchyLabels();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && this.data().results) {

      if (this.data().originalRequest.pageSize === 0) {
        this.data().originalRequest.pageSize = this.data().totalCount;
      }
      this.setExpanded(this.data().results);
      this.detectedAllegati();
    }

    if (changes.activeColumns) {
      this.activeColumnMetadata = this.activeColumns().map(ac => this.allColumnMetadata.find(m => m.name === ac)) as DocumentiListColumnMetadata[];
    }
  }

  lazyLoad(event: TreeTableLazyLoadEvent): void {
    
    const originalRequest = this.data().originalRequest;

    if (originalRequest.pageFirstRow !== event.first || originalRequest.pageSize !== event.rows ||
      originalRequest.sortField !== event.sortField || originalRequest.sortOrder !== event.sortOrder) {

      if (event.sortField === 'PosizioneId') {
        event.sortOrder = 1;
      }

      const request = {
        pageFirstRow: event.first as number,
        pageSize: event.rows,
        sortField: event.sortField,
        sortOrder: event.sortOrder,
      } as DocumentiSearchRequest;

      //se cambia il numero di risultati da visualizzare ritorna alla prima pagina

      if (originalRequest.pageSize !== event.rows || originalRequest.sortField !== event.sortField || originalRequest.sortOrder !== event.sortOrder) { //Ho cambiato il numero di elementi per pagina o l'ordinamento
        request.pageFirstRow = 0;
      }
      this.updateRequestAndSearch.emit(request);
    }
  }

  circleChanged(ev: { filter: string, value: boolean }, posizioneId: string): void {
    const request = {
      filters: this.data().originalRequest.filters
    } as DocumentiSearchRequest;

    if (request.filters.posizioneStato) {
      if (ev.value) {
        request.filters.posizioneStato[posizioneId] = ev.filter;
      } else {
        delete request.filters.posizioneStato[posizioneId];
      }
    }

    this.updateRequestAndSearch.emit(request);
  }

  onRowSelection(e: TreeTableNode<DocumentiSearchDocRow> | null): void {

    const row = e?.node?.data;

    if (this.allowSelection()) {
      if (!row) return;
      this.rowSelected?.emit(row);
    }

    if (this.allowCheckBoxSelection()) {
      if (Array.isArray(this.selectedRow)) {
        this.rowSelected?.emit(this.selectedRow.map(e => e.data as DocumentiSearchDocRow));
      }
    }
  }

  onAllRowsSelection( event: TreeTableHeaderCheckboxToggleEvent): void {
    if ( event.checked) {
       this.rowSelected?.emit(this.data().results.map(e => e.data as DocumentiSearchDocRow));
    } else {
      this.rowSelected?.emit([]);
    }
  }

  selectedDocument(row: DocumentiSearchPosizioneRow): void {
    this.nuovoDocumentoService.setPosizione(row.posizioneTree);
    void this.router.navigate(['nuovo-in-fascicolo']);
  }

  openOrCloseAll(): void {
    this.allExpanded = !this.allExpanded;
    this.setExpanded(this.data().results);
    this.data().results = [...this.data().results];
  }

  getRowLink(col: DocumentiListColumnMetadata, rowData: DocumentiSearchDocRow): unknown[] | null {
    if (col?.linkUrlBuilder) {
      return col.linkUrlBuilder(rowData);
    } else {
      return null;
    }
  }

  getRowTooltip(col: DocumentiListColumnMetadata, rowData: DocumentiSearchDocRow): string | undefined {
    if (col.propName && (rowData[col.propName] as string)?.length > 10) {
      return rowData[col.propName] as string;
    }
  }

  getRowCssClass(rowData: DocumentiSearchDocRow): string | undefined {
    if (this.allowSelection()) {
      return '';
    }

    if (this.enableDateExpirationCss()) {
      return this.getRowCssClassByExpirationDate(rowData);
    }

    if (rowData.statoId === StatiPredefiniti.Evidenza as number) {
      if (rowData.dataScadenza) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        rowData.dataScadenza.setHours(0, 0, 0, 0);

        if (rowData.dataScadenza.toISOString() < today.toISOString()) {
          return 'background-evidenza-scaduta';
        } else if (rowData.dataScadenza.toISOString() === today.toISOString()) {
          return 'background-evidenza-scadenza-oggi';
        }
      }
    } else if (rowData.statoId === StatiPredefiniti.Distrutto as number
      || rowData.statoId === StatiPredefiniti.Versato as number
      || rowData.statoId === StatiPredefiniti.Acquisito as number) {
      return 'background-documento-non-disponibile';
    }
    return '';
  }

  getRowCssClassByExpirationDate(rowData: DocumentiSearchDocRow): string | undefined {
    if (rowData.dataScadenzaClassifica) {
      const daysLeft = Math.round((new Date(rowData.dataScadenzaClassifica).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      if (daysLeft < 0) {
        return 'background-documento-classifica-scaduta';
      }
      if (daysLeft >= 0 && daysLeft <= environment.daysLimit.untilExpirationAlert) {
        return 'background-documento-classifica-in-scadenza';
      }
    }
  }

  actionSetInTrattazione(row: DocumentiSearchDocRow): void {
    this.documentiService.changeState(row.documentoId, StatiPredefiniti.Trattazione, row.statoUtente, '').subscribe(() => this.updateResults());
  }

  actionSetInArchivio(row: DocumentiSearchDocRow): void {
    this.documentiService.changeState(row.documentoId, StatiPredefiniti.Archivio, row.statoUtente, '').subscribe(() => this.updateResults());
  }

  actionProrogaEvidenza(e: { id: number, date: Date }): void {
    this.documentiService.prorogaEvidenza(e.id, e.date).subscribe(() => this.updateResults());
  }

  updateResults(): void {
    const request = {
      invalidateCache: true
    } as DocumentiSearchRequest;
    this.updateRequestAndSearch.emit(request);
  }

  private setExpanded(arr: DocumentiSearchResultRow[]) {
    arr.forEach(leaf => {
      if (leaf.children) {
        leaf.expanded = this.allExpanded;
        this.setExpanded(leaf.children);
      }
    });
  }

  checkActionAllegato(allegato: DocumentiSearchAllegato): void {
    const file_type = allegato.fileName.substring(allegato.fileName.lastIndexOf('.')).toLowerCase();
    // eslint-disable-next-line no-constant-condition
    if (file_type === '.pdf' && false) {
      this.open(allegato);
    } else {
      this.download(allegato);
    }
  }

  open(allegato: DocumentiSearchAllegato): void {
    this.allegatiService.openAllegato(allegato as DocumentoAllegato);
  }

  download(allegato: DocumentiSearchAllegato): void {
    this.allegatiService.downloadAllegato(allegato as DocumentoAllegato);
  }

  setActionableRow(row: DocumentiSearchDocRow): void {
    this.actionableRow = row;
  }

  showDeclassifica(): void {
    this.showDeclassificaDialog.set(true);
  }

  showStateArchive(): void {
    this.showStateArchiveDialog.set(true);
  }

  showModificaStato(row: DocumentiSearchDocRow): void {
    this.setActionableRow(row);
    this.showModificaStatoDialog.set(true);
  }

  showDistruzione(): void {
    this.showDistruzioneDialog.set(true);
    this.distruzioneAllegato = this.actionableRow?.allegati.map(el => ({ allegatoId: el.streamId, pagine: null, fileName: el.fileName }));
  }

  isInCarico(statoId: number): boolean {
    return statoId !== StatiPredefiniti.Distrutto as number
      && statoId !== StatiPredefiniti.Versato as number
      && statoId !== StatiPredefiniti.Annullato as number
      && statoId !== StatiPredefiniti.Spedito as number
      && !this.isAcquisito(statoId);
  }

  isInEvidenza(statoId: number): boolean {
    return statoId === StatiPredefiniti.Evidenza as number;
  }

  isAcquisito(statoId: number) {
    return statoId === StatiPredefiniti.Acquisito as number;
  }

  isInArchivio(statoId: number): boolean {
    return statoId === StatiPredefiniti.Archivio as number;
  }

  private detectedAllegati() {
    (this.data().results?.[0]?.children as TypedTreeNode<DocumentiSearchDocRow>[])?.forEach((el: TypedTreeNode<DocumentiSearchDocRow>) => {
      this.createItems(el);
    });
  }

  private createItems(node: TypedTreeNode<DocumentiSearchDocRow>): void {
    const documento = node.data;
    if (documento?.documentoId) {
      this.items[documento.documentoId] = [];
      documento.allegati.forEach((allegato) => {
        this.items[documento.documentoId].push({ label: allegato?.fileName, icon: 'pi pi-file', state: allegato, command: () => this.checkActionAllegato(allegato) });
      });
    }

    node.children?.forEach(child => {
      this.createItems(child);
    });
  }

  showCopy(row: DocumentiSearchDocRow): void {
    this.setActionableRow(row);
    this.showCopyDialog.set(true);
  }
}
